import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { ListDevice } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { capitalize } from 'lodash-es';
import { scopedFilterAndSort } from '@frontend/filters';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useTimestampFormatter } from '@frontend/timestamp-formatter';
import { theme } from '@frontend/theme';
import { Chip, CopyIcon, EditSimpleIcon, Table, TextLink, Truncated, useModalControl } from '@frontend/design-system';
import { PhoneSettingsScopedLocationFilterMenu } from '../../../components/common/index-table';
import { DuplicateLayoutModal } from '../../../components/devices/line-keys/modals/duplicate-layout.modal';

type Props = {
  devices: ListDevice[] | undefined;
  filteredLocations: string[];
  setFilteredLocations: Dispatch<SetStateAction<string[]>>;
};

export const LineKeys = ({ devices, setFilteredLocations, filteredLocations }: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'line-keys' });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { navigate } = useSettingsNavigate();
  const [deviceToDuplicate, setDeviceToDuplicate] = useState<ListDevice>();
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );

  const { triggerProps, modalProps } = useModalControl();
  const formatTimestamp = useTimestampFormatter();

  const filteredData = useMemo(() => {
    const filtered = scopedFilterAndSort({
      data: devices ?? [],
      filteredScopeIds: filteredLocations,
      scopeIds: globalAvailableLocationIds,
      scopeIdAccessor: (device) => (device.location?.locationId ? [device.location?.locationId] : []),
    });

    return searchTerm
      ? filtered.filter(
          (data) =>
            data.name?.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()) ||
            data.sipProfiles?.some(({ extension }) => extension?.extensionNumber === +searchTerm)
        )
      : filtered;
  }, [devices, filteredLocations, searchTerm, globalAvailableLocationIds]);

  return (
    <>
      <Table
        data={filteredData}
        hasGlobalSearch
        globalSearchConfig={{
          searchHandler: (term) => {
            setSearchTerm(term);
          },
        }}
        customToolbarRender={
          settingsTenantLocation && settingsTenantLocation?.locationType !== 'single'
            ? () => (
                <PhoneSettingsScopedLocationFilterMenu
                  selectedOptions={filteredLocations}
                  onChange={setFilteredLocations}
                />
              )
            : undefined
        }
        colConfig={[
          {
            Header: t('Device Name'),
            id: 'device-name',
            accessor: (cell) => cell.name,
            cellRenderer: (name, device) => {
              return (
                <Truncated
                  as={TextLink}
                  textAlign='left'
                  css={css`
                    width: fit-content;
                  `}
                  onClick={() => {
                    if (device) {
                      navigate({ to: '/phone/line-keys/:id', params: { id: device.deviceId } });
                    }
                  }}
                  trackingId='line-keys-device-text-link'
                >
                  {name}
                </Truncated>
              );
            },
          },
          {
            Header: t('Extension'),
            id: 'extension',
            accessor: ({ sipProfiles }) => sipProfiles?.[0]?.extension?.extensionNumber,

            disableSortBy: true,
            width: 125,
          },
          {
            Header: t('Location'),
            id: 'location',
            accessor: (device) => device,
            cellRenderer: (device) => {
              if (!device.location) return '-';
              return <Chip.SingleChip>{device.location?.name}</Chip.SingleChip>;
            },
            disableSortBy: true,
            omit: settingsTenantLocation?.locationType === 'single',
            width: 150,
          },
          {
            Header: t('Model'),
            id: 'model',
            accessor: ({ make, model }) => {
              if (!model || !make) {
                return (
                  <div css={{ display: 'flex', gap: theme.spacing(1) }}>
                    -
                    <Icon color='warn' name='warning-badge' />
                  </div>
                );
              }
              return capitalize(make?.toLocaleLowerCase()) + ' ' + model?.toLocaleUpperCase();
            },
          },
          {
            Header: t('Last Updated'),
            id: 'last-updated',
            accessor: (cell) => cell.updatedAt,
            cellRenderer: (date) => formatTimestamp(date),
            omit: true,
          },
        ]}
        rowActions={{
          actions: [
            {
              label: t('Edit Line Keys'),
              Icon: EditSimpleIcon,
              onClick: ({ deviceId }) => {
                navigate({ to: '/phone/line-keys/:id', params: { id: deviceId } });
              },
              trackingId: 'edit-device-line-keys',
            },
            {
              label: t('Duplicate Layout'),
              Icon: CopyIcon,
              onClick: (device) => {
                triggerProps.onClick();
                setDeviceToDuplicate(device);
              },
              trackingId: 'duplicate-device-line-keys-layout',
            },
          ],
        }}
        wrapperStyle={css`
          .table-toolbar {
            justify-content: space-between;
          }
        `}
      />
      {modalProps.show && <DuplicateLayoutModal device={deviceToDuplicate ?? ({} as ListDevice)} {...modalProps} />}
    </>
  );
};
